import Swiper from "swiper";
import { A11y, Autoplay, Keyboard } from "swiper/modules";
import type { SwiperEvents } from "swiper/types";
import "swiper/css";
import prefersReducedMotion from "./prefersReducedMotion";

export interface CarouselOptions {
   loop: boolean;   
   gap: { mobile: number; desktop: number };   
   autoRotateAfterMs?: number;
   autoRotateIntervalMs?: number;
}

export default function initCarousel(target: string | HTMLElement, options: CarouselOptions): Swiper | undefined {
   const el = typeof target == "string" ? document.querySelector<HTMLElement>(target) : target;
   if (!el) {
      return undefined;
   }

   const reducedMotion = prefersReducedMotion();
   const autoRotate = !!options.autoRotateAfterMs && !reducedMotion;

   const swiper = new Swiper(el, {
      modules: [A11y, Autoplay, Keyboard],
      slidesPerView: "auto",
      spaceBetween: options.gap.mobile,
      breakpoints: {
         768: {
            spaceBetween: options.gap.desktop,
         },
      },
      loop: options.loop,
      speed: reducedMotion ? 0 : 400,
      watchOverflow: true,
      keyboard: { enabled: true, onlyInViewport: true },
      a11y: { enabled: true },
      autoplay: autoRotate
         ? { delay: options.autoRotateIntervalMs ?? 4000, pauseOnMouseEnter: true, disableOnInteraction: false }
         : false,
   });

   const root = el.closest(".hp3-carousel") ?? el;
   const prevButtons = root.querySelectorAll<HTMLButtonElement>("[data-carousel-prev]");
   const nextButtons = root.querySelectorAll<HTMLButtonElement>("[data-carousel-next]");
   prevButtons.forEach((b) => b.addEventListener("click", () => swiper.slidePrev()));
   nextButtons.forEach((b) => b.addEventListener("click", () => swiper.slideNext()));

   if (!options.loop) {
      const updateButtons = () => {
         prevButtons.forEach((b) => (b.disabled = swiper.isBeginning || swiper.isLocked));
         nextButtons.forEach((b) => (b.disabled = swiper.isEnd || swiper.isLocked));
      };
      updateButtons();
      const events: (keyof SwiperEvents)[] = ["slideChange", "reachBeginning", "reachEnd", "fromEdge", "progress", "lock", "unlock", "resize"];
      events.forEach((e) => swiper.on(e, updateButtons));
   }

   if (autoRotate) {
      // Hold auto-advance until the visitor has been on the page for a while
      swiper.autoplay.stop();
      const timer = window.setTimeout(() => swiper.autoplay.start(), options.autoRotateAfterMs);
      swiper.on("destroy", () => window.clearTimeout(timer));
   }

   return swiper;
}
